/**
 * KI lesend in der Planungsphase (Plan Stufe 7) — die REGELN der Anzeige.
 *
 * Zwei Wege, beide nur Vorschlaege:
 *
 *   7.1 „Aus Unterlagen lesen (KI)": ausgewaehlte PDFs der Projektablage oder
 *       lokal gewaehlte Dateien → je noch ungesetztem Merkmal ein Vorschlag mit
 *       wörtlichem Zitat (`liesMerkmale` im Service `services/planungKi`).
 *   7.2 „Antwortmail einfügen": eingefuegter Mailtext → je offener Frage die
 *       woertliche Antwort (`ordneMailAntworten`).
 *
 * Hier steht, was man ohne DOM, ohne Supabase und ohne KI pruefen kann
 * (`planungKiAnzeige.test.ts`): welche Felder gefragt werden, welche Dokumente
 * ueberhaupt an die KI duerfen, wie die Vorschlaege gruppiert und
 * Widersprueche erkannt werden, was eine Uebernahme am ENTWURF aendert und wie
 * eine Mailantwort an eine bestehende Antwort kommt.
 *
 * ═══ ZUSAGEN ═════════════════════════════════════════════════════════════
 *  - Die KI liest nur. Nichts hier schreibt in die Datenbank; ein Merkmal geht
 *    per Klick in den Entwurf (`uebernehmeKiWert`), gespeichert wird weiter
 *    nur ueber „Grundlagen speichern" — dieselbe Regel wie Stufe 2.1.
 *  - Gefragt wird nur nach Feldern, die der Entwurf noch NICHT gesetzt hat.
 *    Ein ausdrueckliches Ja oder Nein des Anwenders ist eine Entscheidung und
 *    wird nicht ueberredet — auch nicht nachtraeglich: eine Uebernahme auf ein
 *    inzwischen gesetztes Feld aendert nichts.
 *  - Jeder Vorschlag traegt Dokument, Seite und woertliches Zitat. Ohne Zitat
 *    kein Vorschlag (Plan 7.1: „Nein-Vorschläge nur mit Zitat", Zahlen nur
 *    mit Zitat) — die Pruefung steht hier ein zweites Mal, der Service ist
 *    die erste Reihe.
 *  - Widersprechen sich Dokumente zum selben Feld, stehen die Werte
 *    nebeneinander, keiner ist vorausgewaehlt.
 *  - Eine Mailantwort setzt den Antworttext (und, wo noch nichts steht, die
 *    Quelle: Weg E-Mail und Name des Absenders) — den STATUS der Frage setzt
 *    der Mensch. Eine vorhandene Antwort wird nie still ueberschrieben:
 *    Anhaengen oder Ersetzen nur per ausdruecklicher Wahl; Ersetzen setzt
 *    auch die Quelle auf die Mail (`quelleAusMail`).
 *  - An die KI geht nur der eigene Text der Mail, ohne Kopf und ohne
 *    zitierten Verlauf, Adressen unkenntlich (`mailTextFuerKi`): der
 *    Absender wird nur lokal gelesen (`absenderAusMail`, antwortQuelle.ts).
 *
 * Personendaten: `dritterNachPara4` und `gebaeudetyp` werden nie gefragt —
 * dieselbe Grenze wie `PLANUNG_MERKMALE` im KI-Kontext.
 */

import { PLANUNG_MERKMALE } from '../../services/aiAssistContext';
import { BAUBESCHREIBUNG_DOKUMENT, planeMerkmalLauf, Verwerfliste } from '../../services/planungKi/auswertung';
import type {
    KiAntwortVorschlag,
    KiLauf,
    KiMerkmalAnfrage,
    KiMerkmalTyp,
    KiMerkmalVorschlag,
    KiQuelle,
} from '../../services/planungKi/typen';
import { mailTextFuerKi, quelleAusMail, type MailAbsender } from './antwortQuelle';
import { MERKMAL_LABEL } from './fragenkatalog';
import type { GrundlagenEntwurf, MerkmalSchalter } from './planungVorbelegung';
import type { FrageAdressat, FrageStatus, Planungsfrage, ProjektMerkmale } from './types';

// ═══════════════════════════════════════════════════════════════════════════
// FELDER, NACH DENEN GEFRAGT WIRD
// ═══════════════════════════════════════════════════════════════════════════

/** Die Zahlenfelder der Merkmale (Umfang, Baujahr, Tiefen, Hoehen). */
export type MerkmalZahl = {
    [K in keyof ProjektMerkmale]-?: ProjektMerkmale[K] extends number | undefined ? K : never
}[keyof ProjektMerkmale];

export type KiFeld = MerkmalSchalter | MerkmalZahl;

interface JaNeinDefinition {
    typ: 'ja_nein';
    feld: MerkmalSchalter;
}

interface ZahlDefinition {
    typ: 'zahl';
    feld: MerkmalZahl;
    /** Frage an das Modell — ohne Vorschrift, mit Einheit. */
    frage: string;
    /** Lesbarer Name in der Leiste. */
    label: string;
    einheit: string;
    ganzzahl: boolean;
    /** Plausibler Bereich, beide Grenzen einschliesslich. Was ausserhalb
     *  liegt, ist eher eine Verwechslung (Datum, Flaeche, Preis) als ein Wert. */
    min: number;
    max: number;
}

export type KiFeldDefinition = JaNeinDefinition | ZahlDefinition;

const ja = (feld: MerkmalSchalter): JaNeinDefinition => ({ typ: 'ja_nein', feld });

/**
 * Alle Felder, nach denen die KI gefragt werden darf, in der Reihenfolge der
 * Maske (Umfang, Merkmalsgruppen, Zahlen zum Bestand). Die Ja/Nein-Felder
 * sind genau die aus `PLANUNG_MERKMALE` — der Test haelt beide Listen gleich.
 */
export const KI_FELDER: readonly KiFeldDefinition[] = [
    {
        typ: 'zahl', feld: 'arbeitstage', label: 'Bauzeit (Arbeitstage)', einheit: 'Arbeitstage',
        frage: 'Voraussichtliche Bauzeit in Arbeitstagen. Nur angeben, wenn das Dokument Arbeitstage nennt. Wochen oder Monate nicht umrechnen.',
        ganzzahl: true, min: 1, max: 3000,
    },
    {
        typ: 'zahl', feld: 'maxBeschaeftigte', label: 'Beschäftigte gleichzeitig', einheit: 'Personen',
        frage: 'Höchstzahl der gleichzeitig auf der Baustelle Beschäftigten (Personen).',
        ganzzahl: true, min: 1, max: 10000,
    },
    {
        typ: 'zahl', feld: 'personentage', label: 'Umfang (Personentage)', einheit: 'Personentage',
        frage: 'Gesamtumfang der Arbeiten in Personentagen.',
        ganzzahl: true, min: 1, max: 1000000,
    },
    ja('mehrereArbeitgeber'),
    ja('unternehmerOhneBeschaeftigte'),
    // Bauaufgabe
    ja('bestand'), ja('abbruch'), ja('erdarbeiten'), ja('altlastenverdacht'), ja('betriebLaeuftWeiter'), ja('strassenbaustelle'),
    // Verfahren und Geraete
    ja('fertigteilmontage'), ja('kran'), ja('geruest'), ja('ortbeton'), ja('dacharbeiten'), ja('tga'), ja('nachtarbeit'), ja('winterbau'),
    // Besonders gefaehrliche Arbeiten
    ja('freileitung'), ja('gewaesser'), ja('tunnelbau'), ja('taucharbeiten'), ja('druckluft'), ja('sprengarbeiten'),
    ja('strahlung'), ja('gefahrstoffeAnhang2'),
    {
        typ: 'zahl', feld: 'bestandBaujahr', label: 'Baujahr Bestand', einheit: '',
        frage: 'Baujahr des Bestandsgebäudes (vierstellige Jahreszahl).',
        ganzzahl: true, min: 1000, max: 2100,
    },
    {
        typ: 'zahl', feld: 'grabentiefeM', label: 'Größte Baugruben-/Grabentiefe', einheit: 'm',
        frage: 'Größte Baugruben- oder Grabentiefe in Metern.',
        ganzzahl: false, min: 0.1, max: 200,
    },
    {
        typ: 'zahl', feld: 'absturzhoeheM', label: 'Größte Absturzhöhe', einheit: 'm',
        frage: 'Größte Absturzhöhe in Metern.',
        ganzzahl: false, min: 0.1, max: 1000,
    },
];

const definitionFuer = (feld: string): KiFeldDefinition | undefined => KI_FELDER.find(d => d.feld === feld);

/** Wortlaut eines Ja/Nein-Merkmals fuer das Modell — derselbe wie im KI-Kontext. */
const wortlautFuer = (feld: MerkmalSchalter): string =>
    PLANUNG_MERKMALE.find(([k]) => k === feld)?.[1] || MERKMAL_LABEL[feld] || feld;

/** Lesbarer Name eines Feldes in der Leiste (wie in der Maske). */
export const kiFeldLabel = (feld: string): string => {
    const d = definitionFuer(feld);
    if (!d) return feld;
    if (d.typ === 'zahl') return d.label;
    if (d.feld === 'mehrereArbeitgeber') return 'Beschäftigte mehrerer Arbeitgeber';
    return MERKMAL_LABEL[d.feld] || wortlautFuer(d.feld);
};

const istGesetzt = (v: unknown): boolean => v !== undefined && v !== null;

/**
 * Die Anfragen an die KI: jedes Feld aus `KI_FELDER`, das im Entwurf noch
 * nicht gesetzt ist. `false` ist gesetzt (ein ausdrueckliches Nein), `0` auch.
 */
export const kiMerkmalAnfragen = (merkmale: ProjektMerkmale | null | undefined): KiMerkmalAnfrage[] => {
    const m: ProjektMerkmale = merkmale || {};
    return KI_FELDER
        .filter(d => !istGesetzt(m[d.feld]))
        .map(d => (d.typ === 'zahl'
            ? { feld: d.feld, frage: d.frage, typ: 'zahl' as const }
            : { feld: d.feld, frage: `${wortlautFuer(d.feld)}?`, typ: 'ja_nein' as const }));
};

// ═══════════════════════════════════════════════════════════════════════════
// WELCHE DOKUMENTE AN DIE KI DUERFEN
// ═══════════════════════════════════════════════════════════════════════════

// Grenzen. Die des Laufs spiegeln den Service (`services/planungKi/auswertung.ts`,
// der Test haelt sie gleich) — hier stehen sie, damit der Dialog sie VOR dem
// Senden nennt und nicht erst das Ergebnis.

/** Hoechstens so viele Dokumente je Lauf (Ablage und lokale Dateien zusammen). */
export const KI_MAX_DOKUMENTE = 5;
/** Groesste Datei, die im Browser gelesen wird — die Grenze der Ablage. Von
 *  einem PDF mit Textebene geht nur der Text an die KI. */
export const KI_MAX_BYTES = 50 * 1024 * 1024;
export const KI_MAX_LABEL = '50 MB';
/** Ein Scan ohne Textebene geht als ganze Datei an die KI — dafuer gilt die engere Grenze. */
export const KI_MAX_SCAN_BYTES = 13 * 1024 * 1024;
export const KI_MAX_SCAN_LABEL = '13 MB';
/** So viele Seiten je Dokument liest der Service hoechstens. */
export const KI_MAX_SEITEN = 120;
/** Laengster Mailtext fuer 7.2 (Zeichen). */
export const KI_MAIL_MAX_ZEICHEN = 60000;
/** Hoechstens so viele offene Fragen gehen mit einer Mail an die KI. */
export const KI_MAX_FRAGEN = 80;

/**
 * Kategorien, die NIE an die KI gehen (Plan 7.1). Betriebsanweisungen und
 * Gefaehrdungsbeurteilungen gehoeren den ausfuehrenden Firmen — und genau
 * aus ihnen wuerde ein Modell Vorschriften und Massnahmen „ableiten".
 */
export const KI_GESPERRTE_KATEGORIEN: readonly string[] = ['betriebsanweisung', 'gefaehrdungen'];

/** Ausschnitt einer Zeile aus `project_documents`, so viel wie die Auswahl braucht. */
export interface KiAblageDokument {
    id: string;
    kategorie: string;
    titel: string;
    dateiname: string;
    mime_typ?: string | null;
    groesse_bytes: number;
    nur_admin: boolean;
    erzeugt_aus?: string | null;
}

export interface KiZaehler {
    grund: string;
    anzahl: number;
}

export interface KiAblageAuswahl<T extends KiAblageDokument> {
    /** Angeboten; `sperrgrund` gesetzt = sichtbar, aber nicht waehlbar. */
    angeboten: { dokument: T; sperrgrund: string | null }[];
    /** Nicht angeboten, je Grund gezaehlt — steht unter der Liste. */
    nichtAngeboten: KiZaehler[];
}

const istPdf = (name: string | null | undefined, typ: string | null | undefined): boolean =>
    String(typ || '').toLowerCase() === 'application/pdf' || /\.pdf$/i.test(String(name || '').trim());

const mb = (bytes: number): string => `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;

/** Warum eine Datei nicht an die KI darf — oder `null`. Fuer Ablage und lokale Dateien. */
export const kiDateiSperrgrund = (name: string, typ: string | null | undefined, groesse: number): string | null => {
    if (!istPdf(name, typ)) return 'Kein PDF. Gelesen werden nur PDF-Dateien.';
    if (!(groesse > 0)) return 'Die Datei ist leer.';
    if (groesse > KI_MAX_BYTES) return `Zu groß (${mb(groesse)}, höchstens ${KI_MAX_LABEL}).`;
    return null;
};

/** Zaehler je Grund zusammenfassen — Reihenfolge des ersten Auftretens. */
export const fasseZaehlerZusammen = (...listen: readonly (readonly KiZaehler[] | null | undefined)[]): KiZaehler[] => {
    const out: KiZaehler[] = [];
    listen.forEach(liste => (liste || []).forEach(z => {
        if (!z || !(z.anzahl > 0)) return;
        const da = out.find(o => o.grund === z.grund);
        if (da) da.anzahl += z.anzahl; else out.push({ grund: z.grund, anzahl: z.anzahl });
    }));
    return out;
};

/**
 * Die Dokumente der Projektablage fuer den Auswahldialog.
 *
 * Gar nicht angeboten (nur gezaehlt):
 *  - Kategorien `betriebsanweisung`/`gefaehrdungen` — nie an die KI;
 *  - admin-gesperrte (`nur_admin`) — fuer die meisten Leser nicht vorhanden,
 *    und was gesperrt ist, verlaesst das Haus nicht;
 *  - eigene Unterlagen der App (`erzeugt_aus` gesetzt oder Kategorie
 *    `eigene_unterlagen`) — sie stammen aus genau diesen Merkmalen; die KI
 *    bestaetigte sonst nur, was schon eingetragen ist;
 *  - alles, was kein PDF ist.
 * Angeboten, aber gesperrt: PDFs ueber der Groessengrenze.
 */
export const kiAblageAuswahl = <T extends KiAblageDokument>(dokumente: readonly T[] | null | undefined): KiAblageAuswahl<T> => {
    const angeboten: { dokument: T; sperrgrund: string | null }[] = [];
    const nicht: KiZaehler[] = [];
    const zaehle = (grund: string) => { nicht.push({ grund, anzahl: 1 }); };
    (dokumente || []).forEach(d => {
        if (!d) return;
        if (KI_GESPERRTE_KATEGORIEN.includes(d.kategorie)) { zaehle('Betriebsanweisungen und Gefährdungsbeurteilungen gehen nie an die KI'); return; }
        if (d.nur_admin) { zaehle('admin-gesperrt'); return; }
        if (d.erzeugt_aus || d.kategorie === 'eigene_unterlagen') { zaehle('eigene Unterlagen der App (beruhen auf diesen Angaben)'); return; }
        if (!istPdf(d.dateiname, d.mime_typ)) { zaehle('kein PDF'); return; }
        angeboten.push({ dokument: d, sperrgrund: kiDateiSperrgrund(d.dateiname || d.titel, d.mime_typ || 'application/pdf', d.groesse_bytes) });
    });
    return { angeboten, nichtAngeboten: fasseZaehlerZusammen(nicht) };
};

const seitenWort = (n: number): string => `${n} Seite${n === 1 ? '' : 'n'}`;

/**
 * Seitenzahlen als Bereiche: [3, 4, 5, 9] → „3–5, 9". Sortiert, ohne
 * Dubletten, nur ganze Zahlen ab 1.
 */
export const seitenBereiche = (seiten: readonly number[] | null | undefined): string => {
    const liste = Array.from(new Set((seiten || []).filter(n => Number.isInteger(n) && n >= 1))).sort((a, b) => a - b);
    const teile: string[] = [];
    let i = 0;
    while (i < liste.length) {
        let j = i;
        while (j + 1 < liste.length && liste[j + 1] === liste[j] + 1) j++;
        teile.push(j > i ? `${liste[i]} bis ${liste[j]}` : String(liste[i]));
        i = j + 1;
    }
    return teile.join(', ');
};

/**
 * Was die Vorbereitung ueber eine Quelle meldet. `seitenGesamt` und
 * `ohneTextSeiten` sind Ergaenzungen des Service — fehlen sie (aeltere
 * Fassung), gilt die Gesamtzahl als unbekannt und keine Seite als
 * ausgelassen; der Hinweis sagt dann „Gesamtseitenzahl unbekannt".
 */
type KiQuelleMitSeitenstand = Pick<KiQuelle, 'art' | 'seiten'> & {
    seitenGesamt?: number | null;
    ohneTextSeiten?: readonly number[] | null;
    /** Gesetzt, wenn die Quelle gar nicht an die KI gehen kann (Scan zu gross). */
    verworfen?: string | null;
};

/**
 * Die Seiten, die der Service von dieser Quelle TATSAECHLICH als Text an die
 * KI schickt — aus seiner eigenen Planung (`planeMerkmalLauf`;
 * `MerkmalAuftrag.seiten` sind dort „die gesendeten Seiten"), nicht
 * nachgebaut. Greift im Service die Grenze von Seiten bzw. Zeichen je
 * Dokument, stimmt die Zahl hier trotzdem.
 *
 * `null` = nicht ermittelbar (Scan, verworfene Quelle, keine Anfragen,
 * Ausnahme). Dann behauptet die Leiste keine Zahl gesendeter Seiten und
 * leitet eine Kuerzung hoechstens aus `verworfen` ab (`kiSeitenWarnungen`).
 */
export const kiGesendeteSeiten = (quelle: KiQuelle, anfragen: readonly KiMerkmalAnfrage[]): number[] | null => {
    if (!quelle || quelle.art !== 'text' || quelle.verworfen || !anfragen || !anfragen.length) return null;
    try {
        const plan = planeMerkmalLauf([quelle], [...anfragen], new Verwerfliste());
        if (!plan.merkmale.length) return null;
        const auftrag = plan.auftraege.find(a => a.quelle === quelle);
        return auftrag ? auftrag.seiten.map(x => x.seite) : [];
    } catch {
        return null;
    }
};

/** Stand eines vorbereiteten Dokuments fuer das Ergebnis — defensiv gegen fehlende Felder. */
export const kiDokumentStandAus = (
    quelle: KiQuelleMitSeitenstand,
    name: string,
    herkunft: KiDokumentStand['herkunft'],
    /** Tatsaechlich gesendete Seiten (`kiGesendeteSeiten`); `null` = unbekannt. */
    gesendet: readonly number[] | null = null,
): KiDokumentStand => {
    const gesamt = typeof quelle.seitenGesamt === 'number' && Number.isInteger(quelle.seitenGesamt) && quelle.seitenGesamt > 0
        ? quelle.seitenGesamt
        : null;
    const ohneText = Array.from(new Set((quelle.ohneTextSeiten || []).filter(n => Number.isInteger(n) && n >= 1)))
        .sort((a, b) => a - b);
    // Eine Quelle, die nicht an die KI gehen kann, darf nicht als „ging an die
    // KI" dastehen — sie erscheint wie ein Lesefehler: „nicht gelesen — Grund".
    const verworfen = String(quelle.verworfen || '').trim();
    return {
        name,
        herkunft,
        art: quelle.art,
        seitenMitText: (quelle.seiten || []).filter(x => String(x.text || '').trim() !== '').length,
        gelesenSeiten: gesendet
            ? Array.from(new Set(gesendet.filter(n => Number.isInteger(n) && n >= 1))).sort((a, b) => a - b)
            : null,
        seitenGesamt: gesamt,
        ohneTextSeiten: ohneText,
        fehler: verworfen || null,
    };
};

/**
 * Lesbarer Satz zur Quelle nach der Vorbereitung (Textebene oder Scan), mit
 * Gesamtseitenzahl. Gezaehlt werden die TATSAECHLICH gesendeten Seiten; sind
 * sie unbekannt, sagt der Satz das, statt die Seiten mit Text als „an die KI"
 * auszugeben.
 */
export const kiQuellenHinweis = (q: Pick<KiDokumentStand, 'art' | 'seitenMitText' | 'gelesenSeiten' | 'seitenGesamt'>): string => {
    const gesamt = typeof q.seitenGesamt === 'number' && q.seitenGesamt > 0 ? q.seitenGesamt : null;
    if (!q.art) return 'nicht vorbereitet.';
    if (q.art === 'pdf') {
        return `Scan ohne Textebene${gesamt ? ` (${seitenWort(gesamt)})` : ''}. Die ganze Datei ging an die KI; Zitate sind nicht prüfbar.`;
    }
    if (q.gelesenSeiten) {
        const n = q.gelesenSeiten.length;
        return gesamt
            ? `Textebene: ${n} von ${seitenWort(gesamt)} als Text an die KI.`
            : `Textebene: ${seitenWort(n)} als Text an die KI (Gesamtseitenzahl unbekannt).`;
    }
    const mitText = q.seitenMitText > 0 ? q.seitenMitText : 0;
    return `Textebene: ${gesamt ? `${mitText} von ${seitenWort(gesamt)}` : seitenWort(mitText)} mit Text. `
        + 'Wie viele davon an die KI gingen, ist nicht ermittelt.';
};

/** Steht in `verworfen` eine Kappung dieses Dokuments? Rueckfall, wenn die gesendeten Seiten unbekannt sind. */
const gekuerztLautVerworfen = (name: string, verworfen: readonly KiZaehler[] | null | undefined): boolean =>
    (verworfen || []).some(z => !!z && z.grund.includes(`„${name}"`) && z.grund.includes('Seiten ab Seite'));

/**
 * Die gelben Warnungen zu einem Dokument — Seiten, die NICHT gelesen wurden:
 *  - Seiten ohne Textebene (nur bei einer Textquelle — bei einem Scan geht
 *    die ganze Datei an die KI);
 *  - gekuerzt: der Service hat an seiner Grenze je Dokument abgeschnitten.
 *    Bekannt aus den gesendeten Seiten; sonst aus `verworfen` abgeleitet.
 * Was dort steht, hat niemand gelesen — der Anwender muss es wissen.
 */
export const kiSeitenWarnungen = (
    q: Pick<KiDokumentStand, 'name' | 'art' | 'ohneTextSeiten' | 'seitenMitText' | 'gelesenSeiten'>,
    verworfen?: readonly KiZaehler[] | null,
): string[] => {
    if (q.art !== 'text') return [];
    const out: string[] = [];
    const bereiche = seitenBereiche(q.ohneTextSeiten);
    if (bereiche) {
        const eine = !bereiche.includes(',') && !bereiche.includes(' bis ');
        out.push(`${eine ? 'Seite' : 'Seiten'} ${bereiche} ohne Textebene: nicht gelesen. Bitte dort selbst nachsehen.`);
    }
    if (q.gelesenSeiten) {
        const rest = q.seitenMitText - q.gelesenSeiten.length;
        if (rest > 0) {
            out.push(q.gelesenSeiten.length
                ? `Gekürzt: nur ${q.gelesenSeiten.length === 1 ? 'Seite' : 'Seiten'} ${seitenBereiche(q.gelesenSeiten)} gelesen; `
                    + `${rest} weitere ${rest === 1 ? 'Seite' : 'Seiten'} mit Text gingen nicht an die KI (Grenze je Dokument). Bitte dort selbst nachsehen.`
                : 'Gekürzt: Keine Seite ging als Text an die KI (schon die erste ist zu lang). Bitte das Dokument selbst durchsehen.');
        }
    } else if (gekuerztLautVerworfen(q.name, verworfen)) {
        out.push('Gekürzt: Nicht alle Seiten mit Text wurden gelesen (Grenze je Dokument, siehe „Verworfen“). Bitte dort selbst nachsehen.');
    }
    return out;
};

// ═══════════════════════════════════════════════════════════════════════════
// ERGEBNIS EINES LESELAUFS (haelt das Modul, damit ein Reiterwechsel es
// nicht vergisst — gespeichert wird es nie)
// ═══════════════════════════════════════════════════════════════════════════

export interface KiDokumentStand {
    name: string;
    /** `grundlagen`: die Baubeschreibung aus der Maske (Einschätzung). */
    herkunft: 'ablage' | 'datei' | 'grundlagen';
    /** Wie die Quelle an die KI ging; `null`, wenn sie nicht vorbereitet werden konnte. */
    art: KiQuelle['art'] | null;
    /** Seiten mit Text nach der Vorbereitung (bei einem Scan 0). */
    seitenMitText: number;
    /** Seiten, deren Text TATSAECHLICH an die KI ging; `null` = nicht ermittelt. */
    gelesenSeiten: number[] | null;
    /** Seiten des Dokuments insgesamt; `null` = unbekannt. */
    seitenGesamt: number | null;
    /** Seiten ohne (oder mit zu duenner) Textebene, die NICHT gelesen wurden. */
    ohneTextSeiten: number[];
    /** Lesefehler dieses Dokuments — es fehlt dann im Lauf, nie still. */
    fehler: string | null;
}

export interface KiLeseErgebnis {
    projectId: string;
    gelesenAm: string;
    anfragen: KiMerkmalAnfrage[];
    dokumente: KiDokumentStand[];
    /** `null`, wenn es gar nicht zur KI kam (kein Dokument lesbar). */
    lauf: KiLauf<KiMerkmalVorschlag> | null;
    /** Fehler ausserhalb des Laufs (kein Dokument lesbar, Netz, Ausnahme). */
    fehler: string | null;
}

/** Der Fehlersatz eines Ergebnisses — `null`, wenn der Lauf ordentlich endete. */
export const kiErgebnisFehler = (e: Pick<KiLeseErgebnis, 'fehler' | 'lauf'> | null | undefined): string | null => {
    if (!e) return null;
    if (e.fehler) return e.fehler;
    if (e.lauf && e.lauf.fehler) return e.lauf.fehler;
    if (!e.lauf) return 'Die KI wurde nicht befragt.';
    return null;
};

// ═══════════════════════════════════════════════════════════════════════════
// VORSCHLAEGE GRUPPIEREN, WIDERSPRUECHE ERKENNEN
// ═══════════════════════════════════════════════════════════════════════════

export interface KiBeleg {
    vorschlagId: string;
    dokument: string;
    seite: number | null;
    zitat: string;
    /** `false`: das Zitat liess sich nicht gegen den Text pruefen (Scan) —
     *  oder es gibt keines (Einschaetzung). */
    geprueft: boolean;
    /** Nein ohne Zitat aus der Baubeschreibung: die Begruendung der KI.
     *  `zitat` ist dann leer. */
    einschaetzung: string | null;
}

interface KiWertOptionBasis {
    /** Stabil je Feld und Wert (`ki:kran:true`) — „Ausblenden" erkennt die Zeile daran. */
    id: string;
    label: string;
    belege: KiBeleg[];
    /** Mindestens ein Beleg ist nicht pruefbar (Scan). */
    ungeprueft: boolean;
    /** Kein Beleg traegt ein Zitat — der Wert ist nur eingeschaetzt. */
    geschaetzt: boolean;
}

export type KiWertOption =
    | (KiWertOptionBasis & { typ: 'ja_nein'; feld: MerkmalSchalter; wert: boolean; nein: boolean })
    | (KiWertOptionBasis & { typ: 'zahl'; feld: MerkmalZahl; wert: number; nein: false });

export interface KiFeldGruppe {
    feld: KiFeld;
    label: string;
    typ: KiMerkmalTyp;
    /** Sichtbare Werte. Mehr als einer = Widerspruch, keiner vorausgewaehlt. */
    optionen: KiWertOption[];
    /** Die Dokumente nennen verschiedene Werte — gerechnet VOR dem Ausblenden,
     *  damit ein weggeklickter Gegenwert den Rest nicht unbestritten aussehen laesst. */
    widerspruch: boolean;
    /** Wie viele Werte dieses Feldes ausgeblendet sind. */
    ausgeblendet: number;
}

export interface KiAuswertung {
    gruppen: KiFeldGruppe[];
    /** Was die Anzeige selbst verworfen hat (zweite Reihe hinter dem Service). */
    verworfen: KiZaehler[];
}

/** Ist der Wert fuer dieses Feld zulaessig? Liefert den Grund, wenn nicht. */
const wertFehler = (d: KiFeldDefinition, wert: unknown): string | null => {
    if (d.typ === 'ja_nein') return typeof wert === 'boolean' ? null : 'Wert ist kein Ja/Nein';
    if (typeof wert !== 'number' || !Number.isFinite(wert)) return 'Wert ist keine Zahl';
    if (d.ganzzahl && !Number.isInteger(wert)) return 'Zahl unplausibel';
    if (wert < d.min || wert > d.max) return 'Zahl unplausibel';
    return null;
};

const VERWORFEN_NICHT_GEFRAGT = 'nach diesem Feld wurde nicht gefragt';
const VERWORFEN_OHNE_ZITAT = 'ohne wörtliches Zitat';

/** Anzeige eines Wertes: „Ja", „Nein", „82 Arbeitstage", „5,5 m". */
export const kiWertText = (o: Pick<KiWertOption, 'typ' | 'wert' | 'feld'>): string => {
    if (o.typ === 'ja_nein') return o.wert ? 'Ja' : 'Nein';
    const d = definitionFuer(o.feld);
    const zahl = String(o.wert).replace('.', ',');
    return d && d.typ === 'zahl' && d.einheit ? `${zahl} ${d.einheit}` : zahl;
};

/** Herkunft eines Belegs, wie sie in der Leiste steht: „KI · Baubeschreibung.pdf, S. 3". */
export const kiBelegText = (b: Pick<KiBeleg, 'dokument' | 'seite'>): string =>
    `KI · ${String(b.dokument || '').trim() || 'Dokument ohne Namen'}${typeof b.seite === 'number' ? `, S. ${b.seite}` : ''}`;

/**
 * Das Etikett vor dem Zitat. Ein geprueftes Zitat ist Text AUS DEM DOKUMENT
 * (der Service schneidet es aus dem extrahierten Text) — so muss es auch
 * dastehen, nicht wie eine Formulierung der KI. Bei einem Scan ist es die
 * Lesung der KI und ungeprueft; das Etikett sagt das.
 */
export const kiZitatEtikett = (b: Pick<KiBeleg, 'seite' | 'geprueft'> & Partial<Pick<KiBeleg, 'einschaetzung'>>): string => {
    if (b.einschaetzung) return 'Einschätzung der KI (kein Zitat):';
    const seite = typeof b.seite === 'number' ? `, S. ${b.seite}` : '';
    return b.geprueft
        ? `Im Dokument${seite}:`
        : `Lesung der KI aus dem Scan${seite} (nicht geprüft):`;
};

/**
 * Macht aus den Vorschlaegen eines Laufs die Zeilen der Leiste.
 *
 *  - Ein Feld, das der Entwurf inzwischen gesetzt hat, faellt weg — gleich ob
 *    durch Uebernahme oder durch eigene Eingabe (wie 2.1).
 *  - Verworfen (gezaehlt) wird: ein Feld, nach dem nicht gefragt wurde; ein
 *    Wert, der nicht zum Feld passt oder unplausibel ist; ein Vorschlag ohne
 *    Zitat — ausser einem Nein mit Einschaetzung (Baubeschreibung).
 *  - Je Feld eine Gruppe, je verschiedenem Wert eine Option; gleiche Belege
 *    (Dokument, Seite, Zitat) nur einmal.
 *  - Reihenfolge: Felder wie die Maske, Werte Ja vor Nein bzw. aufsteigend —
 *    nie nach „Mehrheit", damit nichts wie vorausgewaehlt aussieht.
 */
export const werteKiVorschlaegeAus = (
    vorschlaege: readonly KiMerkmalVorschlag[] | null | undefined,
    anfragen: readonly KiMerkmalAnfrage[] | null | undefined,
    merkmale: ProjektMerkmale | null | undefined,
    ausgeblendet: ReadonlySet<string> = new Set(),
): KiAuswertung => {
    const m: ProjektMerkmale = merkmale || {};
    const gefragt = new Set((anfragen || []).map(a => a.feld));
    const verworfen: KiZaehler[] = [];
    const verwirf = (grund: string) => { verworfen.push({ grund, anzahl: 1 }); };

    const optionenJeFeld = new Map<string, KiWertOption[]>();
    (vorschlaege || []).forEach(v => {
        if (!v) return;
        const d = definitionFuer(v.feld);
        if (!d || !gefragt.has(v.feld)) { verwirf(VERWORFEN_NICHT_GEFRAGT); return; }
        const fehler = wertFehler(d, v.wert);
        if (fehler) { verwirf(fehler); return; }
        const zitat = String(v.zitat || '').trim();
        // Ohne Zitat nur ein Nein mit Einschaetzung — die zweite Reihe hinter
        // `werteEinschaetzungAus`, das auch die Merkmale ohne Schaetzung kennt.
        const einschaetzung = !zitat && d.typ === 'ja_nein' && v.wert === false
            ? String(v.einschaetzung || '').trim()
            : '';
        if (!zitat && !einschaetzung) { verwirf(VERWORFEN_OHNE_ZITAT); return; }
        if (istGesetzt(m[d.feld])) return;

        const id = `ki:${d.feld}:${String(v.wert)}`;
        const liste = optionenJeFeld.get(d.feld) || [];
        let option = liste.find(o => o.id === id);
        if (!option) {
            const basis = { id, label: kiFeldLabel(d.feld), belege: [], ungeprueft: false, geschaetzt: true };
            if (d.typ === 'ja_nein' && typeof v.wert === 'boolean') {
                option = { ...basis, typ: 'ja_nein', feld: d.feld, wert: v.wert, nein: v.wert === false };
            } else if (d.typ === 'zahl' && typeof v.wert === 'number') {
                option = { ...basis, typ: 'zahl', feld: d.feld, wert: v.wert, nein: false };
            } else {
                return; // durch `wertFehler` ausgeschlossen
            }
            liste.push(option);
            optionenJeFeld.set(d.feld, liste);
        }
        const dokument = String(v.dokument || '').trim();
        const seite = typeof v.seite === 'number' && Number.isFinite(v.seite) ? v.seite : null;
        const doppelt = option.belege.some(b => b.dokument === dokument && b.seite === seite && b.zitat === zitat
            && (b.einschaetzung || '') === einschaetzung);
        if (!doppelt) {
            option.belege.push({ vorschlagId: v.id, dokument, seite, zitat, geprueft: v.geprueft === true, einschaetzung: einschaetzung || null });
            // Eine Einschaetzung ist kein ungepruefter Scan — sie hat ihr eigenes Etikett.
            if (v.geprueft !== true && !einschaetzung) option.ungeprueft = true;
            if (zitat) option.geschaetzt = false;
        }
    });

    const gruppen: KiFeldGruppe[] = [];
    KI_FELDER.forEach(d => {
        const alle = optionenJeFeld.get(d.feld);
        if (!alle || !alle.length) return;
        const sortiert = [...alle].sort((a, b) => (a.typ === 'ja_nein'
            ? Number(b.wert) - Number(a.wert)
            : Number(a.wert) - Number(b.wert)));
        const sichtbar = sortiert.filter(o => !ausgeblendet.has(o.id));
        if (!sichtbar.length) return;
        gruppen.push({
            feld: d.feld,
            label: kiFeldLabel(d.feld),
            typ: d.typ,
            optionen: sichtbar,
            widerspruch: sortiert.length > 1,
            ausgeblendet: sortiert.length - sichtbar.length,
        });
    });

    return { gruppen, verworfen: fasseZaehlerZusammen(verworfen) };
};

/**
 * Uebernimmt EINEN Wert in den Entwurf — dieselbe Wirkung wie eine Eingabe in
 * der Maske, nicht mehr. Ist das Feld inzwischen gesetzt, bleibt der Entwurf
 * unveraendert (Objektidentitaet bleibt): ein Ja oder Nein des Anwenders wird
 * nicht ueberschrieben.
 */
export type KiUebernahme =
    | { typ: 'ja_nein'; feld: MerkmalSchalter; wert: boolean }
    | { typ: 'zahl'; feld: MerkmalZahl; wert: number };

export const uebernehmeKiWert = <E extends GrundlagenEntwurf>(entwurf: E, option: KiUebernahme): E => {
    const merkmale = entwurf.merkmale || {};
    if (istGesetzt(merkmale[option.feld])) return entwurf;
    const d = definitionFuer(option.feld);
    if (!d || d.typ !== option.typ || wertFehler(d, option.wert)) return entwurf;
    const neu: E = { ...entwurf, merkmale: { ...merkmale } };
    if (option.typ === 'ja_nein') neu.merkmale[option.feld] = option.wert;
    else neu.merkmale[option.feld] = option.wert;
    return neu;
};

/**
 * „Alle übernehmen": je Feld die eine sichtbare Option — Felder mit
 * Widerspruch bleiben aussen vor, dort entscheidet der Mensch einzeln.
 */
export const eindeutigeOptionen = (gruppen: readonly KiFeldGruppe[]): KiWertOption[] =>
    (gruppen || []).filter(g => !g.widerspruch && g.optionen.length === 1).map(g => g.optionen[0]);

// ═══════════════════════════════════════════════════════════════════════════
// BAUBESCHREIBUNG AUSWERTEN (Betreiber 29.10.2026)
// ═══════════════════════════════════════════════════════════════════════════

/** Was bei „Baubeschreibung auswerten" an die KI geht: Baubeschreibung und
 *  ergänzende Angaben der Maske, sonst nichts aus dem Projekt. */
export const kiBeschreibungText = (baubeschreibung: string | null | undefined, bauaufgabe: string | null | undefined): string =>
    [baubeschreibung, bauaufgabe].map(t => String(t || '').trim()).filter(Boolean).join('\n\n');

/** Ergebnis einer Einschätzung im Format der Leiste — die „Quelle" ist die Maske. */
export const kiBeschreibungErgebnis = (
    projectId: string,
    anfragen: readonly KiMerkmalAnfrage[],
    lauf: KiLauf<KiMerkmalVorschlag> | null,
    gelesenAm: string,
    fehler: string | null = null,
): KiLeseErgebnis => ({
    projectId,
    gelesenAm,
    anfragen: [...anfragen],
    dokumente: [{
        name: BAUBESCHREIBUNG_DOKUMENT,
        herkunft: 'grundlagen',
        art: 'text',
        seitenMitText: 1,
        gelesenSeiten: [1],
        seitenGesamt: 1,
        ohneTextSeiten: [],
        fehler: null,
    }],
    lauf,
    fehler,
});

// ═══════════════════════════════════════════════════════════════════════════
// 7.2 ANTWORTMAIL
// ═══════════════════════════════════════════════════════════════════════════

/** Erledigt — eine solche Frage geht nicht an die KI. Wie der Filter „unerledigt". */
const ERLEDIGT: readonly FrageStatus[] = ['beantwortet', 'gegenstandslos'];

export interface KiFrage {
    code: string;
    frage: string;
}

/**
 * Die Fragen, die mit der Mail an die KI gehen: nur unerledigte (offen,
 * gestellt, teilbeantwortet, nicht beantwortet), je Code einmal, nur Code und
 * Fragetext — keine Antworten, keine Bewertung, keine Empfaenger. Mit
 * `adressat` nur die Fragen an diesen Adressaten: eine Mail kommt meist von
 * EINEM Beteiligten, und weniger Fragen heisst weniger Fehlzuordnungen (und
 * bleibt unter `KI_MAX_FRAGEN`).
 */
export const offeneFragenFuerKi = (
    fragen: readonly (Pick<Planungsfrage, 'code' | 'frage' | 'status'> & Partial<Pick<Planungsfrage, 'adressat'>>)[] | null | undefined,
    adressat?: FrageAdressat | null,
): KiFrage[] => {
    const out: KiFrage[] = [];
    const gesehen = new Set<string>();
    (fragen || []).forEach(f => {
        if (!f || ERLEDIGT.includes(f.status)) return;
        if (adressat && !(f.adressat || []).includes(adressat)) return;
        const code = String(f.code || '').trim().toUpperCase();
        const frage = String(f.frage || '').trim();
        if (!code || !frage || gesehen.has(code)) return;
        gesehen.add(code);
        out.push({ code, frage });
    });
    return out;
};

/** Warum der Mailtext so nicht gesendet wird — oder `null`. Gesendet wird
 *  nur der eigene Text der Mail, ohne Kopf und zitierten Verlauf
 *  (`mailTextFuerKi`); bleibt davon nichts, gibt es nichts zuzuordnen. */
export const mailTextSperrgrund = (text: string | null | undefined): string | null => {
    const t = String(text || '').trim();
    if (!t) return 'Bitte den Text der Antwortmail einfügen.';
    if (t.length > KI_MAIL_MAX_ZEICHEN) {
        return `Der Text ist zu lang (${t.length.toLocaleString('de-DE')} Zeichen, höchstens ${KI_MAIL_MAX_ZEICHEN.toLocaleString('de-DE')}). Bitte auf die Antwort kürzen.`;
    }
    if (!mailTextFuerKi(t)) {
        return 'Außer Mailkopf (Von, An, Betreff) und zitiertem Verlauf steht kein eigener Text in der Mail. Bitte die Antwort selbst mit einfügen.';
    }
    return null;
};

export interface KiAntwortZeile {
    vorschlagId: string;
    frageId: string;
    code: string;
    frage: string;
    /** Die woertliche Antwort aus der Mail. */
    antwort: string;
}

/**
 * Ordnet die Antworten der KI den Fragen zu. Verworfen (gezaehlt) wird eine
 * Antwort ohne Text und eine zu einem Code, der nicht unter den
 * mitgeschickten offenen Fragen war — die KI darf keine Frage „finden", die
 * sie nicht bekommen hat. Reihenfolge wie die Fragenliste.
 */
export const ordneKiAntwortenZu = (
    vorschlaege: readonly KiAntwortVorschlag[] | null | undefined,
    fragen: readonly Pick<Planungsfrage, 'id' | 'code' | 'frage'>[] | null | undefined,
    gesendet: readonly KiFrage[] | null | undefined,
): { zeilen: KiAntwortZeile[]; verworfen: KiZaehler[] } => {
    const codes = new Set((gesendet || []).map(g => g.code.toUpperCase()));
    const verworfen: KiZaehler[] = [];
    const roh: KiAntwortZeile[] = [];
    (vorschlaege || []).forEach(v => {
        if (!v) return;
        const code = String(v.code || '').trim().toUpperCase();
        const antwort = String(v.antwort || '').trim();
        if (!antwort) { verworfen.push({ grund: 'Antwort ohne Text', anzahl: 1 }); return; }
        const frage = (fragen || []).find(f => String(f.code || '').trim().toUpperCase() === code);
        if (!codes.has(code) || !frage) { verworfen.push({ grund: 'Code gehört zu keiner mitgeschickten offenen Frage', anzahl: 1 }); return; }
        if (roh.some(z => z.frageId === frage.id && z.antwort === antwort)) return;
        roh.push({ vorschlagId: v.id, frageId: frage.id, code: frage.code, frage: frage.frage, antwort });
    });
    const reihenfolge = (id: string) => (fragen || []).findIndex(f => f.id === id);
    const zeilen = [...roh].sort((a, b) => reihenfolge(a.frageId) - reihenfolge(b.frageId));
    return { zeilen, verworfen: fasseZaehlerZusammen(verworfen) };
};

export type AntwortModus = 'anhaengen' | 'ersetzen';

/** Braucht die Uebernahme eine ausdrueckliche Wahl? Ja, sobald schon eine Antwort steht. */
export const brauchtModusWahl = (bisher: string | null | undefined): boolean => String(bisher || '').trim().length > 0;

/** Steht die Mailantwort schon woertlich in der Antwort? Dann gibt es nichts anzuhaengen. */
export const antwortSchonEnthalten = (bisher: string | null | undefined, neu: string | null | undefined): boolean => {
    const n = String(neu || '').trim();
    return !!n && String(bisher || '').includes(n);
};

/**
 * Der neue Antworttext — oder `null`, wenn ohne Wahl nicht entschieden werden
 * darf (es steht schon eine Antwort und kein Modus ist gewaehlt).
 *
 *   leer       → die Mailantwort, gleich welcher Modus;
 *   anhaengen  → bisherige Antwort wortgetreu, Leerzeile, Mailantwort — steht
 *                sie schon darin, bleibt alles, wie es ist;
 *   ersetzen   → nur die Mailantwort.
 */
export const antwortMitMail = (
    bisher: string | null | undefined,
    neu: string,
    modus: AntwortModus | null,
): string | null => {
    const n = String(neu || '').trim();
    const alt = String(bisher || '');
    if (!n) return null;
    if (!brauchtModusWahl(alt)) return n;
    if (modus === 'ersetzen') return n;
    if (modus === 'anhaengen') {
        if (antwortSchonEnthalten(alt, n)) return alt;
        return `${alt.replace(/\s+$/, '')}\n\n${n}`;
    }
    return null;
};

/** Was eine Uebernahme aus der Mail an der Frage aendert: der Antworttext,
 *  dazu hoechstens die Quelle (Weg, Absender, beim Ersetzen das Loesen des
 *  Belegs) — nie ein Status. */
export type KiAntwortAenderung = Pick<Planungsfrage, 'antwort'>
    & Partial<Pick<Planungsfrage, 'antwort_von' | 'antwort_weg' | 'antwort_beleg_id'>>;

/**
 * Die Aenderung an der Frage: der Antworttext. Den Status setzt der
 * Mensch — die Funktion liefert nie ein `status`, auch kein `beantwortet_am`.
 * `null`, wenn nichts zu aendern ist oder die Wahl fehlt.
 *
 * Mit `mail` (die Uebernahme aus „Antwortmail einfügen") kommt die Quelle
 * dazu (`quelleAusMail`): Weg E-Mail und der Name des lokal gelesenen
 * Absenders. Beim Setzen einer leeren Antwort und beim Anhaengen nur dort,
 * wo an der Frage noch nichts steht; ERSETZT die Mail eine vorhandene
 * Antwort, ersetzt sie auch deren Quelle (ohne Absender: „Antwort von"
 * leer) und loest den Beleg.
 */
export const kiAntwortAenderung = (
    frage: Pick<Planungsfrage, 'antwort'> & Partial<Pick<Planungsfrage, 'antwort_von' | 'antwort_weg'>>,
    neu: string,
    modus: AntwortModus | null,
    mail?: { absender: MailAbsender | null } | null,
): KiAntwortAenderung | null => {
    const antwort = antwortMitMail(frage.antwort, neu, modus);
    if (antwort === null || antwort === String(frage.antwort || '')) return null;
    if (!mail) return { antwort };
    // Ersetzt wird nur, wo schon eine Antwort stand — sonst ist es ein erstes Setzen.
    const ersetzen = modus === 'ersetzen' && brauchtModusWahl(frage.antwort);
    return { antwort, ...quelleAusMail(frage, mail.absender, { ersetzen }) };
};
